import { Paper } from '@mantine/core';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { forwardRef, useCallback, useMemo } from 'react';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { useVenueMapHandlers } from 'src/components/Listings/MapWithCompListingsPreview/MapWithCompListingsPreview.hooks';
import { Content } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useMobileListingDrawerContext } from 'src/contexts/MobileListingDrawerContext/MobileListingDrawerContext';
import {
  defaultListingSortingCriteria,
  useLocalEventMobileScrollContext,
  useMobileInventorySortAndFilterContext,
} from 'src/contexts/MobileSortAndFilterContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import {
  ActionBarItem,
  MobileFloatingActionBar,
} from 'src/core/ui/MobileFloatingActionBar/MobileFloatingActionBar';
import { ScrollableToolbar } from 'src/core/ui/ScrollableToolbar';
import { useComparableListingsVenueMapColor } from 'src/hooks/useComparableListingsVenueMapColor';
import { MapIcon } from 'src/svgs';
import { ChevronDownIcon, ChevronUpIcon } from 'src/svgs/Viagogo';
import { ListingGroupStatusSummary } from 'src/tables/ListingTable/configs/ListingGroupStatusSummary';
import { ContentId } from 'src/utils/constants/contentId';
import {
  CompListing,
  GroupType,
  Listing,
  ListingGroup,
  SectionInfo,
} from 'src/WebApiController';

import { SelectedSectionItem } from '../components/MobileDrawerVenueMap';
import * as styles from './MobileListingGroupDetailsView.css';

export const ListingGroupFloatingActions = ({
  showAllInventory,
  toggleAllInventory,
  showMap,
  toggleMap,
}: {
  showAllInventory: boolean;
  toggleAllInventory: () => void;
  showMap: boolean;
  toggleMap: () => void;
}) => {
  const { itemSelection } = useMultiSelectionContext();
  const { selectedListing } = useMobileListingDrawerContext();
  const { setSortingCriteria } = useMobileInventorySortAndFilterContext();
  const { isAtBottom, isAtTop } = useLocalEventMobileScrollContext();

  const actions: ActionBarItem[] = useMemo(
    () => [
      {
        id: 'map',
        content: (
          <MapIcon
            fill={showMap ? vars.color.textBrand : vars.color.textSecondary}
          />
        ),
        onClick: toggleMap,
        className: clsx(styles.inventoryFloatingActionMap, {
          [styles.inventoryFloatingActionActive]: showMap,
        }),
      },
      {
        id: 'all-listings',
        content: (
          <span
            style={{
              color: showAllInventory
                ? vars.color.textBrand
                : vars.color.textSecondary,
            }}
          >
            <Content id={ContentId.AllListings} />
          </span>
        ),
        type: 'button',
        onClick: () => {
          toggleAllInventory();
          // Reset sorting criteria for all inventory
          if (!showAllInventory) {
            setSortingCriteria(defaultListingSortingCriteria);
          }
        },
        className: clsx(styles.inventoryFloatingActionMap, {
          [styles.inventoryFloatingActionActive]: showAllInventory,
        }),
      },
    ],
    [
      showMap,
      toggleMap,
      showAllInventory,
      toggleAllInventory,
      setSortingCriteria,
    ]
  );

  const showFloatingBar = useMemo(() => {
    if (!isEmpty(itemSelection) || selectedListing) {
      return false;
    }
    return true;
  }, [itemSelection, selectedListing]);

  const hasOverlay = useMemo(() => {
    return isAtBottom && !isAtTop;
  }, [isAtBottom, isAtTop]);

  return (
    <>
      {showFloatingBar && !hasOverlay && (
        <MobileFloatingActionBar actions={actions} />
      )}
    </>
  );
};

// Sub group tile for the second layer group
export const MobileListingSubGroupTile = ({
  listingGroup,
  expanded,
  onUpdateExpanded,
}: {
  listingGroup: ListingGroup;
  expanded: boolean;
  onUpdateExpanded: (expanded: boolean) => void;
}) => {
  const [isValidSubGroup, groupType] = useMemo(() => {
    const { groupType } = listingGroup;
    const isValidSubGroup = groupType != null && groupType !== GroupType.Leader;

    return [isValidSubGroup, groupType];
  }, [listingGroup]);

  if (!isValidSubGroup) {
    return null;
  }

  return (
    <Paper
      withBorder
      shadow="sm"
      p={vars.spacing.m}
      radius={vars.borderRadius.m}
      bg={vars.color.backgroundPrimary}
      onClick={() => onUpdateExpanded(!expanded)}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="spaceBetween"
        style={{ color: vars.color.textPrimary }}
      >
        <Stack direction="row" alignItems="center" gap="m">
          <ListingGroupStatusSummary listingGroup={listingGroup} />
          <span>
            {groupType === GroupType.Active ? (
              <Content id={ContentId.Active} />
            ) : groupType === GroupType.Inactive ? (
              <Content id={ContentId.Queued} />
            ) : groupType === GroupType.FullySold ? (
              <Content id={ContentId.Sold} />
            ) : groupType === GroupType.Invisible ? (
              <Content id={ContentId.HiddenDueToFilter} />
            ) : (
              listingGroup.groupName
            )}
          </span>
        </Stack>
        <Stack className={styles.secondaryInfo} gap="m" alignItems="center">
          {(listingGroup.unitCst?.disp ?? listingGroup.faceValue?.disp) &&
            `(${listingGroup.unitCst?.disp ?? listingGroup.faceValue?.disp})`}
          {expanded ? (
            <ChevronUpIcon
              fill={vars.color.textSecondary}
              size={vars.iconSize.s}
            />
          ) : (
            <ChevronDownIcon
              fill={vars.color.textSecondary}
              size={vars.iconSize.s}
            />
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

type ListingGroupVenueMapProps = {
  eventListings: Listing[];
  compListings: CompListing[];
  selectedSections: SectionInfo[];
  onUpdateSelectedSections: (updated: SectionInfo[]) => void;
};
export const ListingGroupVenueMap = forwardRef<
  HTMLDivElement,
  ListingGroupVenueMapProps
>(function ListingGroupVenueMapRaw(
  { eventListings, compListings, selectedSections, onUpdateSelectedSections },
  ref
) {
  const { venueMapInfo } = useEventMapContext();

  const {
    onSectionClicked,
    onToggleMirrors,
    onSectionHovered,
    onSectionTapAndHold,
  } = useVenueMapHandlers(
    selectedSections,
    undefined,
    venueMapInfo,
    onUpdateSelectedSections
  );

  // Disable rainbow color when there is section selections in the map
  const disableRainbowColor = useMemo(() => {
    return !isEmpty(selectedSections);
  }, [selectedSections]);

  const getColor = useComparableListingsVenueMapColor(
    eventListings ?? [],
    compListings,
    undefined,
    disableRainbowColor
  );

  const markedSectionIds = useMemo(
    () => selectedSections.map((section: SectionInfo) => section.id),
    [selectedSections]
  );

  const onDeleteSection = useCallback(
    (sectionIdToDelete: number) => {
      const updated = selectedSections.filter(
        (s) => s.id !== sectionIdToDelete
      );
      onUpdateSelectedSections(updated);
    },
    [selectedSections, onUpdateSelectedSections]
  );

  const sectionsContent = useMemo(() => {
    if (!isEmpty(selectedSections)) {
      return (
        <ScrollableToolbar
          scrollAreaClassName={styles.scrollalbeArea}
          scrollButtonClassName={styles.scrollButton}
        >
          <Stack gap="s" style={{ flex: 1 }}>
            {selectedSections.map((section) => {
              if (!section) {
                return null;
              }
              return (
                <SelectedSectionItem
                  key={section.id}
                  section={section}
                  onDelete={() => onDeleteSection(section.id)}
                />
              );
            })}
          </Stack>
        </ScrollableToolbar>
      );
    }

    return undefined;
  }, [onDeleteSection, selectedSections]);

  return (
    <Stack
      direction="column"
      width="full"
      className={styles.container}
      ref={ref}
    >
      {sectionsContent && (
        <Stack alignItems="start" style={{ maxWidth: '100%' }}>
          <div className={styles.recommendation}>{sectionsContent}</div>
        </Stack>
      )}
      <div style={{ width: '100%', aspectRatio: '7/6' }}>
        <EventVenueMap
          isCondensedView
          selectedSectionIds={[]}
          markedSectionIds={markedSectionIds}
          onSectionClicked={onSectionClicked}
          onSectionTapAndHold={onSectionTapAndHold}
          onSectionHovered={onSectionHovered}
          onToggleMirrors={onToggleMirrors}
          setSelectedSections={onUpdateSelectedSections}
          getColor={getColor}
          allowDrawLine={false}
          condensedControl
          mapActionContainerClassname={styles.actionContainer}
        />
      </div>
    </Stack>
  );
});
